import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Store } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider.jsx';
import { ApiError } from '../api/client.js';
import { env } from '../lib/env.js';
import { t } from '../lib/i18n.js';

/**
 * Active store indicator. For a seller assigned to several stores it doubles
 * as a switcher so they can change the store the session operates on without
 * logging out.
 */
function StoreSwitcher() {
  const { storeSelection, activeStoreId, selectStore } = useAuth();
  const [busy, setBusy] = useState(false);
  const stores = (storeSelection && storeSelection.stores) || [];
  if (!storeSelection || !storeSelection.scoped || stores.length === 0) return null;

  if (stores.length === 1) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm text-ink-muted">
        <Store className="h-4 w-4" strokeWidth={1.75} />
        {stores[0].name}
      </span>
    );
  }

  const onChange = async (e) => {
    const id = e.target.value;
    if (!id || id === activeStoreId) return;
    setBusy(true);
    try {
      await selectStore(id);
      toast.success('Negozio attivo aggiornato.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Impossibile cambiare negozio.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="inline-flex items-center gap-1.5 text-ink-muted">
      <Store className="h-4 w-4" strokeWidth={1.75} />
      <select
        value={activeStoreId || ''}
        onChange={onChange}
        disabled={busy}
        aria-label="Negozio attivo"
        className="h-8 rounded-md border border-border bg-surface-elevated px-2 text-sm text-ink focus:border-ink focus:outline-none"
      >
        {stores.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
      </select>
    </span>
  );
}

const NAV = [
  { to: '/employee', label: 'Dashboard', end: true },
  { to: '/employee/inventory', label: 'Inventario', perm: 'products:read' },
  { to: '/employee/chat', label: 'Assistente AI', perm: 'ai:chat' },
  { to: '/employee/account', label: 'Account' }
];

export function EmployeeLayout() {
  const { user, hasPermission, logout } = useAuth();
  const navigate = useNavigate();
  const onLogout = async () => { await logout(); navigate('/login', { replace: true }); };

  return (
    <div className="flex h-screen flex-col bg-surface">
      <header className="flex items-center justify-between border-b border-border bg-surface-elevated px-6 py-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">{env.appName}</p>
          <p className="text-sm font-medium text-ink">Dashboard dipendente</p>
        </div>
        <nav className="flex items-center gap-2">
          {NAV.filter((n) => !n.perm || hasPermission(n.perm)).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => clsx(
                'rounded-md px-3 py-1.5 text-sm transition-colors',
                isActive ? 'bg-ink text-white' : 'text-ink-muted hover:bg-border/40 hover:text-ink'
              )}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <StoreSwitcher />
          <span className="text-sm text-ink-muted">{user && user.email}</span>
          <button type="button" onClick={onLogout} className="text-sm text-brand hover:text-brand-hover">
            {t('common.logout')}
          </button>
        </div>
      </header>
      <main className="flex-1 overflow-y-auto px-6 py-6">
        <Outlet />
      </main>
    </div>
  );
}
